/**
 * The popup behind an Email Queue row: the email as the recipient gets it, with
 * where it stands alongside. Its links open in a new tab.
 */
import { useEffect, useRef, useState } from 'react';
import { emailQueueAPI, type EmailQueueEntryDetail } from '../../lib/api/emailQueue';
import EmailPreviewFrame from '../../components/admin/settings/emailTemplates/EmailPreviewFrame';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { StatusBadge, effectiveStatus, humanise, timeAgo } from './EmailQueueRow';
import { queueErrorMessage } from './useEmailQueue';

interface EmailQueueDetailDialogProps {
  /** The entry to show; null keeps the popup shut. */
  entryId: number | null;
  onClose: () => void;
}

const when = (date: string | null): string | null => (date ? timeAgo(date) : null);

export function EmailQueueDetailDialog({ entryId, onClose }: EmailQueueDetailDialogProps) {
  const [entry, setEntry] = useState<EmailQueueEntryDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const lastEntryId = useRef(entryId);

  useEffect(() => {
    if (entryId === null) return undefined;
    lastEntryId.current = entryId;
    let live = true;
    setEntry(null);
    setError(null);
    setLoading(true);
    emailQueueAPI.getEntry(entryId)
      .then((response) => { if (live) setEntry(response.data); })
      .catch((err: unknown) => {
        if (live) setError(queueErrorMessage(err, 'Failed to load this email'));
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [entryId, attempt]);

  const facts: Array<[string, string | number | null]> = entry
    ? [
      ['Recipient side', entry.recipient_side ? humanise(entry.recipient_side) : null],
      ['Customer', entry.customer_name || null],
      ['Type', humanise(entry.type_key)],
      ['Created', timeAgo(entry.created_at)],
      ['Sent', when(entry.sent_at)],
      ['Delivered', when(entry.delivered_at)],
      ['Opened', when(entry.opened_at)],
      ['Clicked', when(entry.clicked_at)],
      ['Cancelled', entry.cancelled_at
        ? `${timeAgo(entry.cancelled_at)}${entry.cancelled_by_email ? ` by ${entry.cancelled_by_email}` : ''}`
        : null],
      ['Retries', `${entry.retry_count} of ${entry.max_retries}`],
    ]
    : [];
  const problem = entry?.bounced_at
    ? `Bounced: ${entry.bounce_reason || 'no reason given'}`
    : entry?.error_message;

  return (
    <Dialog open={entryId !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="flex max-h-[90vh] max-w-4xl flex-col gap-3 overflow-y-auto"
        onCloseAutoFocus={(event) => {
          // Back to the row it came from, which the ten-second refresh may have redrawn.
          event.preventDefault();
          document.querySelector<HTMLElement>(`[data-entry-id="${lastEntryId.current}"]`)?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="break-words pr-6 text-base">{entry?.subject ?? 'Email'}</DialogTitle>
          <DialogDescription className="break-all">
            {entry
              ? `To ${entry.recipient_email}${entry.recipient_company_name ? ` · ${entry.recipient_company_name}` : ''}`
              : error ? 'This email could not be loaded' : 'Loading this email'}
          </DialogDescription>
        </DialogHeader>

        {entry && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <StatusBadge
              status={effectiveStatus(entry)}
              opened={Boolean(entry.delivered_at && entry.opened_at)}
            />
            {facts.filter(([, value]) => value !== null).map(([label, value]) => (
              <span key={label} className="text-muted-foreground">
                {label}: <span className="text-foreground">{value}</span>
              </span>
            ))}
          </div>
        )}
        {problem && <p className="break-words text-sm text-red-600">{problem}</p>}

        <EmailPreviewFrame
          title="What the recipient sees"
          preview={entry
            ? { subject: '', html: entry.rendered_html, attachments: entry.attachments }
            : null}
          loading={loading}
          error={error}
          onRetry={() => setAttempt((current) => current + 1)}
          emptyHint="Choose an email from the queue."
          linksOpen
        />
      </DialogContent>
    </Dialog>
  );
}
